import React from 'react';
import makeStyles from '@material-ui/styles/makeStyles';

const useStyles = makeStyles(() => ({
    card: {
        border: '1px solid #e9e5e1',
        borderRadius: 16,
        padding: '13px 14px',
        marginBottom: 10,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 10,
    },
    titleWrap: { minWidth: 0 },
    title: { fontWeight: 700, fontSize: 14 },
    description: { fontSize: 11, color: '#74777d', marginTop: 3 },
    actions: { display: 'flex', gap: 6, flexShrink: 0 },
    editBtn: {
        width: 28, height: 28, borderRadius: 8,
        border: '1px solid #f6e1b5', background: '#fdf1dc',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 800, color: '#b0740a', cursor: 'pointer',
    },
    deleteBtn: {
        width: 28, height: 28, borderRadius: 8,
        border: '1px solid #f6d8d3', background: '#fdecea',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
    },
    deleteIcon: {
        width: 11, height: 11,
        borderTop: '3px solid #c0392b',
        borderLeft: '1.6px solid #c0392b',
        borderRight: '1.6px solid #c0392b',
        borderBottom: '1.6px solid #c0392b',
        borderRadius: '0 0 2px 2px',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 10, marginTop: 12, paddingTop: 11,
        borderTop: '1px solid #f5f2ef',
    },
    label: {
        fontSize: 9, letterSpacing: '0.05em',
        textTransform: 'uppercase', color: '#74777d', fontWeight: 700,
    },
    val: { fontWeight: 700, fontSize: 13.5, marginTop: 3 },
    highlightVal: { fontWeight: 800, fontSize: 13.5, marginTop: 3, color: '#b0740a' },
    empty: {
        border: '1px dashed #e9e5e1',
        borderRadius: 16,
        padding: '32px 14px',
        textAlign: 'center',
        color: '#74777d',
        fontSize: 13,
    },
}));

/**
 * Reusable card component for displaying an item with title, description,
 * edit/delete actions, and a grid of name–value pairs.
 *
 * @param {string}   title       - Card heading
 * @param {string}   description - Subtitle / secondary text
 * @param {function} onEdit      - Edit click handler (omit to hide button)
 * @param {function} onDelete    - Delete click handler (omit to hide button)
 * @param {Array<{name:string, value:*, highlight?:boolean}>} items - Grid items
 * @param {boolean}  disabled    - Dims card and disables interaction
 * @param {string}   emptyMessage - Text shown when no title/items (empty state)
 */
const ItemCard = ({ title, description, onEdit, onDelete, items, disabled, emptyMessage }) => {
    const classes = useStyles();

    if (!title && (!items || items.length === 0) && emptyMessage) {
        return <div className={classes.empty}>{emptyMessage}</div>;
    }

    const showActions = !disabled && (onEdit || onDelete);

    return (
        <div className={classes.card} style={disabled ? { opacity: 0.6, pointerEvents: 'none' } : {}}>
            <div className={classes.header}>
                <div className={classes.titleWrap}>
                    <div className={classes.title}>{title}</div>
                    {description && <div className={classes.description}>{description}</div>}
                </div>
                {showActions && (
                    <div className={classes.actions}>
                        {onEdit && (
                            <div className={classes.editBtn} onClick={onEdit}>✎</div>
                        )}
                        {onDelete && (
                            <div className={classes.deleteBtn} onClick={onDelete}>
                                <div className={classes.deleteIcon} />
                            </div>
                        )}
                    </div>
                )}
            </div>
            {items && items.length > 0 && (
                <div className={classes.grid}>
                    {items.map((item, idx) => {
                        const isLast = idx === items.length - 1;
                        const posInRow = idx % 3;
                        const span = isLast && posInRow !== 2 ? 3 - posInRow : 1;
                        return (
                            <div key={idx} style={span > 1 ? { gridColumn: `span ${span}` } : undefined}>
                                <div className={classes.label}>{item.name}</div>
                                <div className={item.highlight ? classes.highlightVal : classes.val}>
                                    {item.value}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ItemCard;
